import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { Router } from './router.js';
import { WebI18nProvider } from '../../shared/i18n/web.js';
import { DisplaySettingsProvider } from '../../shared/hooks/useDisplaySettings.js';
import { useTenantIdentity } from './shell/useTenantIdentity.js';
import { TenantThemeSync } from './theme/TenantThemeSync.js';

export interface AppProps {
  /** Test-only: use a MemoryRouter with optional initial entries. */
  readonly initialEntries?: readonly string[];
}

/**
 * Publishes the signed-in tenant's display settings to every screen.
 *
 * The host adapter for `DisplaySettingsProvider`, in the same spirit as
 * `WebI18nProvider` is for the locale: the shared layer holds the values, the
 * host knows where to get them. It must sit inside `AuthProvider` because the
 * settings arrive with the session, and `useTenantIdentity` shares one cached
 * request with the header, so this costs no extra call.
 */
function TenantDisplaySettings({ children }: { readonly children: ReactNode }): ReactNode {
  const { token } = useAuth();
  const identity = useTenantIdentity(token);
  return (
    <DisplaySettingsProvider
      dateFormat={identity?.dateFormat}
      numberFormat={identity?.numberFormat}
    >
      {children}
    </DisplaySettingsProvider>
  );
}

/**
 * Top-level TradeLogic Admin app. Composes i18n + Router providers + AuthProvider.
 * Test-only: pass `initialEntries` to use a MemoryRouter for in-memory routing.
 */
export function App({ initialEntries }: AppProps): ReactNode {
  const inner = (
    <WebI18nProvider>
      <AuthProvider>
        <TenantThemeSync />
        <TenantDisplaySettings>
          <Router />
        </TenantDisplaySettings>
      </AuthProvider>
    </WebI18nProvider>
  );
  if (initialEntries) {
    return <MemoryRouter initialEntries={[...initialEntries]}>{inner}</MemoryRouter>;
  }
  return <BrowserRouter>{inner}</BrowserRouter>;
}
